import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Video, Calendar, ExternalLink, Loader2, Users, Clock,
  Timer, MapPin, Link2,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { apiFetch } from '../../fetchInterceptor';
import { useWorkspace } from '../../context/WorkspaceContext';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function _formatLocalTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

/**
 * TodaysMeetingsCard — Command Center widget that surfaces today's
 * Google Calendar events. Renders nothing when the user hasn't
 * connected Google for this workspace OR when the server-side OAuth
 * client isn't configured — so the dashboard layout stays clean for
 * users who don't use the integration.
 */
export const TodaysMeetingsCard = () => {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const wsId = currentWorkspace?.workspace_id;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [creatingFor, setCreatingFor] = useState(null);

  useEffect(() => {
    if (!wsId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const r = await apiFetch(
          `${API_URL}/api/integrations/google/calendar/today?workspace_id=${wsId}&tz=${encodeURIComponent(tz)}`,
        );
        if (!cancelled && r.ok) setData(await r.json());
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [wsId]);

  const createTimeEntry = async (event) => {
    if (!wsId) return;
    setCreatingFor(event.event_id);
    try {
      const r = await apiFetch(
        `${API_URL}/api/integrations/google/calendar/time-entry?workspace_id=${wsId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event_id: event.event_id,
            description: event.title,
            billable: true,
          }),
        },
      );
      const body = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(
          `Logged ${body.time_entry?.hours}h for "${event.title}"`,
          {
            action: {
              label: 'View',
              onClick: () => navigate('/time-tracking'),
            },
          },
        );
      } else {
        toast.error(body.detail || 'Could not create time entry.');
      }
    } finally {
      setCreatingFor(null);
    }
  };

  // Hide the entire widget when Google isn't connected. Keeps the
  // dashboard tidy for users who don't use the integration.
  if (loading) {
    return null;
  }
  if (!data || data.connected === false) {
    return null;
  }

  const events = data.events || [];

  return (
    <section data-testid="cc-todays-meetings">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-sky-100 dark:bg-sky-950/40 flex items-center justify-center">
            <Calendar className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400" />
          </div>
          <h3 className="text-sm font-semibold tracking-tight">Today&apos;s meetings</h3>
          <Badge variant="outline" className="text-[10px]">
            {events.length} {events.length === 1 ? 'event' : 'events'}
          </Badge>
        </div>
        <a
          href="https://calendar.google.com/calendar/u/0/r/day"
          target="_blank" rel="noreferrer"
          className="text-[11px] text-sky-600 hover:text-sky-700 inline-flex items-center gap-1"
        >
          Open Calendar <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <Card>
        <CardContent className="py-3 space-y-1.5">
          {events.length === 0 ? (
            <div className="text-xs text-zinc-500 dark:text-zinc-400 py-2 text-center">
              Nothing on the calendar today. Enjoy the deep work.
            </div>
          ) : events.map((e) => (
            <div
              key={e.event_id}
              className={cn(
                'flex items-start gap-3 py-2 px-2 rounded-md group',
                e.in_progress
                  ? 'bg-emerald-50 dark:bg-emerald-950/30 ring-1 ring-emerald-200 dark:ring-emerald-900/40'
                  : 'hover:bg-zinc-50 dark:hover:bg-zinc-900/40',
              )}
              data-testid={`cc-meeting-${e.event_id}`}
            >
              <div className="w-14 shrink-0 text-right pt-0.5">
                <div className="text-xs font-semibold text-zinc-800 dark:text-zinc-200">
                  {e.is_all_day ? 'All day' : _formatLocalTime(e.start)}
                </div>
                {!e.is_all_day && e.duration_minutes && (
                  <div className="text-[10px] text-zinc-500">{e.duration_minutes}m</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="text-sm font-medium truncate">{e.title}</div>
                  {e.in_progress && (
                    <Badge className="text-[9px] bg-emerald-600 text-white px-1.5 h-4 shrink-0">
                      live
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-zinc-500 dark:text-zinc-400">
                  {e.attendee_count > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-2.5 h-2.5" /> {e.attendee_count}
                    </span>
                  )}
                  {e.location && !e.location.startsWith('http') && (
                    <span className="inline-flex items-center gap-1 truncate max-w-[180px]">
                      <MapPin className="w-2.5 h-2.5" /> {e.location}
                    </span>
                  )}
                  {e.meet_link && (
                    <a
                      href={e.meet_link} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sky-600 hover:text-sky-700"
                      data-testid={`cc-meeting-join-${e.event_id}`}
                    >
                      <Video className="w-2.5 h-2.5" /> Join
                    </a>
                  )}
                </div>
              </div>
              {e.duration_minutes && (
                <Button
                  size="sm" variant="ghost"
                  onClick={() => createTimeEntry(e)}
                  disabled={creatingFor === e.event_id}
                  data-testid={`cc-meeting-time-entry-${e.event_id}`}
                  className="h-7 text-[10px] opacity-70 group-hover:opacity-100 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-100 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
                >
                  {creatingFor === e.event_id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <><Timer className="w-3 h-3 mr-1" /> Log time</>
                  )}
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  );
};
